import * as React from 'react';
import type { strokeDashTypes } from '@react-financial-charts/core';
import { isDefined } from '@react-financial-charts/core';
import { saveNodeType } from 'react-financial-charts';
import { TPAndSLDragHandle, PriceLine } from '.';
import { OrderEntryBox, calcEntryBoxWidth } from './OrderEntryBox';
import { TPAndSLBox, TP_AND_SL_BOX_W } from './TPAndSLBox';
import { TPSLRatioBox } from './TPSLRatioBox';
import { EdgeCircleAndVerticalLine } from './EdgeCircleAndVerticalLine';
import { measureTextWidth } from '@/utils/canvasUtils';
import {
  calculateTPAndSL,
  calculateTPFromRatio,
  TPSLRatio,
  netRoeToPrice,
  priceToNetRoe,
} from '@/utils/calculateSLndTP';
import { ShapeType } from '@/types/commontypes';
import type { EdgeConfig, MoreProps, RatioDropdown } from '@/types/commontypes';
import { globalShapeStore } from '@/stores/globalShapeStore';
import { COLORS } from '@/constants/colors';

// TPAndSLDragHandle 너비 공식: padding.left + measureText + padding.right
export function calcLabelWidth(text: string, font: string, padLeft = 6, padRight = 6): number {
  return padLeft + measureTextWidth(text, font) + padRight;
}

interface TextBoxSection {
  readonly right: number;
  readonly padding: { left: number; right: number };
  readonly [key: string]: unknown;
}

export interface EachInteractiveOrderLineProps {
  readonly index?: number;
  /** 도형 고유 UUID */
  readonly uuid?: string;
  /** 드래그 가능 여부 */
  readonly draggable: boolean;
  /** 진입가 (주문 라인의 Y축 위치) */
  readonly yValue: number;
  /** 주문 수량 */
  readonly amount: number;
  readonly tpslPrice: Array<{ tp: number; sl: number }>;
  readonly bgFill: string;
  /** 라인 색상 */
  readonly stroke: string;
  /** 라인 두께 */
  readonly strokeWidth: number;
  /** 라인 점선 스타일 */
  readonly strokeDasharray: strokeDashTypes;
  /** 텍스트 색상 */
  readonly textFill: string;
  /** 폰트 굵기 */
  readonly fontWeight: string;
  /** 폰트 패밀리 */
  readonly fontFamily: string;
  /** 폰트 스타일 (normal, italic 등) */
  readonly fontStyle: string;
  /** 폰트 크기 */
  readonly fontSize: number;
  /** 포지션 방향 (long: 매수, short: 매도) */
  readonly positionType: 'long' | 'short';
  /** 현재 선택 상태 */
  readonly selected: boolean;
  /** 라인 끝단(edge) 스타일 설정 */
  readonly edge: EdgeConfig;
  /** 텍스트 박스 레이아웃 설정 (닫기 아이콘, 포지션 타입, 가격 표시 영역) */
  readonly textBox: {
    readonly closeIcon: TextBoxSection;
    readonly selectedType: TextBoxSection;
    readonly coordinateValue: TextBoxSection;
  };
  /** 드래그 중 호출. type으로 진입가/TP/SL 중 어느 라인을 드래그하는지 구분 */
  readonly onDrag?: (
    e: React.MouseEvent,
    index: number | undefined,
    yCoordinateValue: number,
    type: 'SL' | 'TP' | 'Entry',
  ) => void;
  /** 드래그 완료 시 호출 */
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps, type: 'SL' | 'TP' | 'Entry') => void;
  /** 삭제 시 호출. Entry: 주문 라인 전체 삭제. TP/SL: X버튼 → tpslPrice 배열 전체 삭제(개별 삭제 없음) */
  readonly onDelete?: (
    e: React.MouseEvent | null,
    index: number | undefined,
    moreProps: MoreProps,
    type: 'SL' | 'TP' | 'Entry',
  ) => void;
  /** 더블클릭 시 호출 (수정 다이얼로그 표시용) */
  readonly onDoubleClick?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  /** TP:SL 비율 드롭다운 위치/표시 상태 변경 (hover/click 신호용) */
  readonly setRatioDropdown: (data: Partial<RatioDropdown>) => void;
  /** 다크 모드 여부 */
  readonly isDark?: boolean;
  /**
   * z-order 2-패스 렌더링용 플래그 (InteractiveOrderLine에서 설정).
   * - false(기본): PriceLine·InteractiveStraightLine 등 선 계열만 렌더
   * - true: Entry/Ratio/TP/SL 박스·핸들 계열만 렌더
   * → 모든 노드의 선을 먼저 그린 뒤 박스를 그려야 박스가 항상 위에 표시된다.
   */
  readonly renderBoxesOnly?: boolean;
  /** IYC(부모)가 공통 주황 진입선을 렌더링할 때 true → 개별 진입선 숨김 */
  readonly hideEntryLine?: boolean;
  /** 헷지 페어 멤버일 때 true → 숏 박스를 차트 좌측에 배치 */
  readonly isHedge?: boolean;
  /** true이면 개별 ratio박스를 숨김 (헷지 공통 ratio박스가 렌더링될 때) */
  readonly hideRatioBox?: boolean;
  /** 거래소에서 실제 포지션이 진입된 상태 */
  readonly isEntered?: boolean;
}

interface EachInteractiveOrderLineState {
  /** 진입가 라인(Entry PriceLine)에 마우스가 올라온 상태.
   * TP/SL 미배치 시 SL/TP 핸들이 entry와 같은 y 위치에 있어 RFC가 onDragStart를 동시에 발화한다.
   * handleDragStart에서 lineHover=true면 SL/TP dragStart를 entry 클릭의 부산물로 보고 무시하는 데 사용. */
  lineHover: boolean;
  /** Entry 박스(OrderEntryBox)에 마우스가 올라온 상태 */
  positionHover: boolean;
  /** TP/SL 라인·핸들 영역에 마우스가 올라온 상태 */
  isSLTPHover: boolean;
  /** ratio 드롭다운이 열린 상태 */
  showRatioDropdown: boolean;
  /** ratio 박스 전체 영역(드래그 영역 포함) 호버 상태 — 서클 표시용 */
  showRatioDragArea: boolean;
  // isDropDownHover: boolean; // 미사용 — onRatioHover/setRatioDropdown으로 대체됨
  /** SL을 한 번이라도 드래그 완료한 상태 — ratio 텍스트 표시 방식 결정에 사용 */
  SLDragCompleted: boolean;
  /** SL 라인이 드래그로 설정되었는지 — re-render 트리거가 필요해 state로 관리 */
  SLdragInProgress: boolean;
  /** TP 라인이 드래그로 설정되었는지 — re-render 트리거가 필요해 state로 관리 */
  TPdragInProgress: boolean;
  /** ratio 선택 또는 SL 드래그 완료 시 계산된 TP 가격 — props.tpsl보다 먼저 반영되는 로컬 값 */
  updatedTP?: number;

  /** getDerivedStateFromProps에서 역산한 TP netROE (%, 표시용) */
  TP_roe?: number;
  /** getDerivedStateFromProps에서 역산한 SL netROE (%, 표시용) */
  SL_roe?: number;
  // ─── getDerivedStateFromProps 변경 감지용 이전 값 스냅샷 ───────────
  prevYValue?: number;
  prevSL?: number;
  prevTP?: number;
  prevAmount?: number;
  prevPositionType?: string;
  prevIsHedge?: boolean;
  /** 현재 표시 중인 TP:SL 비율 — ratio 박스 텍스트 및 드롭다운 선택 반영에 사용 */
  tpslRatio: number;
}

export class EachInteractiveOrderLine extends React.Component<
  EachInteractiveOrderLineProps,
  EachInteractiveOrderLineState
> {
  public static defaultProps = {
    strokeWidth: 1,
    selected: false,
    draggable: false,
  };

  // ─── 드래그 상태 ───────────────────────────────────────────────
  /** 드래그 시작 시점의 가격·마우스 오프셋·타입 스냅샷 — handleDrag에서 delta 계산에 사용 */
  private dragStartPosition: { yValue: number; dy: number; type: 'Entry' | 'TP' | 'SL' } | undefined;
  /** RFC의 saveNodeType 바인딩 — InteractiveStraightLine·TPAndSLDragHandle에 ref를 붙여 RFC 내부 히트 테스트에 인스턴스를 등록 */
  private saveNodeType: <T>(key: string | Record<string, unknown>, subKey?: string) => React.Ref<T>;
  /** render()에서 계산된 TPSLRatioBox rightOffset — onRatioClick/onRatioHover에서 boxX 계산에 사용 */
  private ratioRightToYAxisDistance = 0;
  /** SL 드래그 중 실시간 Y값 (setState 없이 관리해 리렌더로 인한 드래그 중단 방지) */
  private draggingSLValueRef: number | undefined = undefined;
  /** TP 드래그 중 실시간 Y값 (setState 없이 관리해 리렌더로 인한 드래그 중단 방지) */
  private draggingTPValueRef: number | undefined = undefined;
  /** 드래그 진행 중 여부 (setState 제거 — render에서 사용 금지, setState 호출 없이 관리) */
  private isDraggingRef = false;
  /** 현재 활성 드래그 타입 — handleDragComplete에서 교차 완료 이벤트를 걸러내는 데 사용 */
  private activeDragType: 'Entry' | 'TP' | 'SL' | undefined = undefined;
  /** SL 제자리 드롭 감지용 pointerup 리스너 cleanup — componentWillUnmount에서 호출 */
  private slPointerUpCleanup: (() => void) | undefined = undefined;
  /** TP 제자리 드롭 감지용 pointerup 리스너 cleanup — componentWillUnmount에서 호출 */
  private tpPointerUpCleanup: (() => void) | undefined = undefined;
  /** Entry 박스 ref — canvasDraw에서 직접 hitTest 수행 (amIOnTop 경쟁 우회) */
  private orderEntryBoxRef = React.createRef<OrderEntryBox>();
  /** TP 박스 ref — canvasDraw에서 직접 hitTest 수행 */
  private tpBoxRef = React.createRef<TPAndSLBox>();
  /** SL 박스 ref — canvasDraw에서 직접 hitTest 수행 */
  private slBoxRef = React.createRef<TPAndSLBox>();
  /** ratio 박스 ref — 드롭다운 위치 계산에 사용 */
  private ratioBoxRef = React.createRef<TPSLRatioBox>();
  /** Entry 삭제를 다음 틱으로 미루는 타이머 — TP/SL이 같은 틱에 오면 취소 */
  private _entryDeleteTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  public constructor(props: EachInteractiveOrderLineProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);

    // localStorage에서 복원된 tpsl의 실제 값(0이 아닌)이 있으면 drag 플래그 초기화하여
    // 페이지 새로고침 후에도 TP/SL 라인이 올바른 위치에 표시되도록 한다.
    const firstPair = props.tpslPrice[0];
    const hasSL = firstPair != null && firstPair.sl !== 0;
    const hasTP = firstPair != null && firstPair.tp !== 0;

    // entry 생성 시점부터 lastTPSLByDirection의 방향별 비율을 ratio 박스 초기값으로 사용
    const { lastTPSLByDirection } = globalShapeStore.getState();
    const dir = props.positionType === 'short' ? 'short' : 'long';
    const isHedgeRatioActive = props.isHedge && lastTPSLByDirection.long === lastTPSLByDirection.short;
    // ?? 2: 앱 첫 실행 등 스토어에 저장된 값이 없을 때 기본 비율을 2.0으로 설정
    const initialRatio = isHedgeRatioActive ? (lastTPSLByDirection.hedge ?? 2) : (lastTPSLByDirection[dir] ?? 2);

    this.state = {
      lineHover: false,
      positionHover: false,
      isSLTPHover: false,
      showRatioDropdown: false,
      showRatioDragArea: false,
      SLDragCompleted: hasSL,
      SLdragInProgress: hasSL,
      TPdragInProgress: hasTP,
      tpslRatio: initialRatio,
    };
  }

  public componentWillUnmount() {
    // SL/TP 핸들 클릭 시 document에 붙인 pointerup 리스너를 제거한다.
    // react-financial-charts는 마우스가 움직이지 않으면 onDragComplete를 호출하지 않아
    // 제자리 드롭을 감지하기 위해 직접 document에 리스너를 붙이는데,
    // 컴포넌트가 언마운트될 때 제거하지 않으면 사라진 컴포넌트의 setState가 호출되어 에러가 난다.
    this.slPointerUpCleanup?.();
    this.tpPointerUpCleanup?.();
    clearTimeout(this._entryDeleteTimer);
  }

  // ─── 렌더링: Entry/TP/SL 라인 + 라벨 + 비율 드롭다운 ─────────
  public render() {
    const {
      yValue,
      fontStyle,
      selected,
      stroke,
      strokeDasharray,
      strokeWidth,
      edge,
      textBox,
      positionType,
      amount,
      tpslPrice,
      isDark,
      hideEntryLine,
      hideRatioBox,
      isEntered,
      renderBoxesOnly,
    } = this.props;

    const { updatedTP, TP_roe, SL_roe, tpslRatio, positionHover, isSLTPHover } = this.state;

    // 원형 핸들 표시 여부:
    // entry 박스(positionHover)·TP/SL 박스(isSLTPHover)·ratio 박스(showRatioDropdown) 위에 마우스가 올라올 때 표시.
    // lineHover는 2-패스 구조상 lines 인스턴스의 상태가 boxes 인스턴스에 전달되지 않아 미사용.
    const showCircle = positionHover || isSLTPHover || this.state.showRatioDropdown || this.state.showRatioDragArea;

    // Entry 박스 오른쪽 끝 → 차트 우측 거리 (textBox.closeIcon.right = rightOffset prop)
    const entryRightToYAxisDistance = textBox.closeIcon.right;
    // Entry 박스 왼쪽 끝 → 차트 우측 거리 (TP/SL 핸들·서클의 기준점)
    const entryBoxWidth = calcEntryBoxWidth(amount, positionType);
    const entryLeftToYAxisDistance = entryRightToYAxisDistance + entryBoxWidth;

    // tpslPrice[0]: 현재 노드의 TP/SL 절대 가격. updatedTP는 드래그 중 실시간 값 우선
    const firstTpsl = tpslPrice[0];
    const tp = firstTpsl?.tp;
    const sl = firstTpsl?.sl;
    const newTP = updatedTP ?? tp;

    // TP/SL PriceLine 오른쪽 끝(Y축)에 붙는 택(◁) 모양 레이블 박스 스타일.
    // edge prop을 기반으로 stroke 색상만 TP/SL 색으로 오버라이드.
    const edgeTP = {
      ...edge,
      stroke: COLORS.tp,
    };

    const edgeSL = {
      ...edge,
      stroke: COLORS.sl,
    };

    // draggingSLValueRef/draggingTPValueRef: 드래그 중 마우스 Y 가격을 ref로 관리.
    // setState 대신 ref를 쓰는 이유: mousemove마다 setState하면 re-render가 발생해
    // RFC가 드래그 이벤트를 놓치거나 라인이 끊기는 문제가 생긴다.
    // canvasDraw가 mousemove마다 직접 ref를 읽어 부드럽게 렌더한다.
    const activeSL = this.draggingSLValueRef ?? sl; // 드래그 중이면 실시간 값, 아니면 props 값

    const activeTP = this.draggingTPValueRef ?? newTP; // 드래그 중이면 실시간 값, 아니면 props 값

    // TP 박스·핸들 표시 여부.
    // - newTP가 entry와 다른 위치에 설정된 상태: props 기준으로 판단해 ratio 드롭다운 등 외부 변경도 즉시 반영
    // - draggingTPValueRef가 있는 상태: 드래그 중 아직 props에 반영 안 됐지만 박스·핸들을 바로 표시해야 함
    const tpIsFixed = (newTP !== undefined && newTP !== 0 && newTP !== yValue) || this.draggingTPValueRef !== undefined;

    // SL 박스·핸들 표시 여부. SL은 드래그 시작 시 SLdragInProgress=true(setState)로 판단하므로
    // draggingSLValueRef 조건 불필요. tpLine 계산 전에 먼저 계산하는 이유는 slIsFixed에 따라
    // TP 핸들의 rightOffset이 달라지기 때문.
    const slIsFixed = sl !== undefined && sl !== 0 && sl !== yValue;

    // ratio박스 rightOffset 계산
    const tpHandleW = calcLabelWidth('TP', `bold ${this.props.fontSize}px ${this.props.fontFamily}`);
    const slHandleW = calcLabelWidth('SL', `bold ${this.props.fontSize}px ${this.props.fontFamily}`);
    let ratioRightOffset: number;
    if (!slIsFixed) {
      ratioRightOffset = entryLeftToYAxisDistance + 8 + slHandleW + 8;
    } else if (!tpIsFixed) {
      ratioRightOffset = entryLeftToYAxisDistance + 8 + tpHandleW + 8;
    } else {
      ratioRightOffset = entryLeftToYAxisDistance + 8;
    }
    this.ratioRightToYAxisDistance = ratioRightOffset;

    // SL이 설정됐을 때 TP 핸들 라벨을 렌더링
    const tpHandle = slIsFixed && (
      <>
        {/* tp-circle은 renderBoxesOnly 블록의 EdgeCircleAndLine이 담당 */}

        {/* TP 핸들 라벨:
            - tpIsFixed: TPAndSLBox 바로 왼쪽에 밀착 (흰 배경 + 초록 글씨)
            - !TPdragInProgress (TP 미설정 or X로 삭제): 단독 핸들로 드래그 가능 (초록 배경 + 흰 글씨)
            - SL 드래그 중에는 표시 안 함 */}
        {tpIsFixed &&
          activeTP !== undefined &&
          this.renderLabel({
            yValue: activeTP,
            text: 'TP',
            textFill: COLORS.tp,
            bgOverride: isDark ? COLORS.bgDark : COLORS.bgLight,
            borderRadius: 4,
            corners: 'left',
            fontWeightOverride: 'bold',
            strokeWidthOverride: 1,
            strokeSides: 'no-right',
            rightOffset: entryRightToYAxisDistance + (this.tpBoxRef.current?.totalW ?? TP_AND_SL_BOX_W),
            strokeStyle: stroke,
            onClick: undefined,
            dragType: 'TP',
            fontStyle: fontStyle,
            onHover: this.handleSLTPHover,
          })}
      </>
    );

    // SL 핸들 라벨을 항상 렌더링 (고정 여부에 따라 스타일·위치 다름)
    const slHandle = (
      <>
        {/* sl-circle은 renderBoxesOnly 블록의 EdgeCircleAndLine이 담당 */}

        {/* SL 핸들 라벨: 항상 유지 (드래그 중 끊김 방지). 고정 시엔 TPAndSLBox 바로 왼쪽에 붙음 */}
        {this.renderLabel({
          yValue: slIsFixed ? (activeSL ?? yValue) : yValue,
          text: 'SL',
          textFill: slIsFixed ? COLORS.sl : COLORS.bgLight,
          bgOverride: slIsFixed ? (isDark ? COLORS.bgDark : COLORS.bgLight) : COLORS.sl,
          borderRadius: 4,
          corners: slIsFixed ? 'left' : 'all',
          fontWeightOverride: 'bold',
          strokeWidthOverride: 1,
          strokeSides: slIsFixed ? 'no-right' : 'all',
          rightOffset: slIsFixed
            ? entryRightToYAxisDistance + (this.slBoxRef.current?.totalW ?? TP_AND_SL_BOX_W)
            : entryLeftToYAxisDistance + 8,
          strokeStyle: slIsFixed ? stroke : COLORS.sl,
          onClick: undefined,
          dragType: 'SL',
          fontStyle: fontStyle,
          onHover: this.handleSLTPHover,
        })}
      </>
    );

    const selectRatio = tpslRatio > 0 ? tpslRatio.toFixed(2) : undefined;

    return (
      <g>
        {/* ── 선(line) 계열: 박스보다 먼저 그려야 박스가 위에 올라옴 ── */}
        {/* RFC는 마우스 이벤트마다 mount 순서대로 캔버스를 다시 그린다.
            unmount → remount 시 구독 순서가 맨 뒤로 밀려 박스 위로 올라오므로,
            숨길 때는 unmount 대신 strokeWidth:0으로 투명하게 처리한다.
            hideEntryLine(헷지 페어 확정 시 개별 진입선 숨김)도 동일하게 항상 마운트 유지. */}
        {!renderBoxesOnly && yValue && (
          <PriceLine
            key="entry-line"
            yValue={yValue}
            strokeStyle={stroke}
            strokeDasharray={strokeDasharray}
            strokeWidth={strokeWidth}
            edge={edge}
            visible={!hideEntryLine}
            textFill={stroke}
            fontFamily={this.props.fontFamily}
            fontSize={this.props.fontSize}
            enableDragOnHover={!isEntered && !hideEntryLine}
            onDragStart={
              isEntered || hideEntryLine ? undefined : (e, moreProps) => this.handleDragStart(e, moreProps, 'Entry')
            }
            onDrag={isEntered || hideEntryLine ? undefined : this.handleDrag}
            onDragComplete={
              isEntered || hideEntryLine ? undefined : (e, moreProps) => this.handleDragComplete(e, moreProps, 'Entry')
            }
            onHover={hideEntryLine ? undefined : this.handleLineHover}
            onUnHover={hideEntryLine ? undefined : this.handleLineHover}
          />
        )}
        {/* TP/SL PriceLine(점선): 조건부 mount 대신 항상 유지 — mount 순서 고정으로 z-order 보장
            보이지 않아야 할 때는 strokeWidth:0으로 숨김 (unmount 시 구독 순서가 밀려 박스 위로 올라오는 문제 방지)
            선 패스(renderBoxesOnly=false)와 박스 패스(renderBoxesOnly=true)는 별개 인스턴스이므로
            선 패스에서는 state가 아닌 props.tpsl로 표시 여부를 판단한다. */}
        {!renderBoxesOnly && (
          <>
            {/* TP 점선: props.tp가 유효한 값일 때 표시 */}
            {(() => {
              const propsTp = tpslPrice[0]?.tp;
              const showTpLine = propsTp !== undefined && propsTp !== 0 && propsTp !== yValue;
              const tpLineY = this.draggingTPValueRef ?? activeTP ?? propsTp ?? yValue;
              return (
                <PriceLine
                  key="tp-price-line"
                  visible={showTpLine}
                  yValue={showTpLine ? tpLineY : yValue}
                  strokeStyle={COLORS.tp}
                  strokeDasharray={strokeDasharray}
                  strokeWidth={strokeWidth}
                  edge={showTpLine ? edgeTP : {}}
                  textFill={COLORS.tp}
                  fontFamily={this.props.fontFamily}
                  fontSize={this.props.fontSize}
                  onDragStart={(e, moreProps) => this.handleDragStart(e, moreProps, 'TP')}
                  onDrag={this.handleDrag}
                  onDragComplete={(e, moreProps) => this.handleDragComplete(e, moreProps, 'TP')}
                  onHover={this.handleSLTPHover}
                  onUnHover={this.handleSLTPHover}
                />
              );
            })()}
            {/* SL 점선: props.sl이 유효한 값일 때 표시 */}
            {(() => {
              const propsSl = tpslPrice[0]?.sl;
              const showSlLine = propsSl !== undefined && propsSl !== 0 && propsSl !== yValue;
              const slLineY = this.draggingSLValueRef ?? activeSL ?? propsSl ?? yValue;
              return (
                <PriceLine
                  key="sl-price-line"
                  visible={showSlLine}
                  yValue={showSlLine ? slLineY : yValue}
                  strokeStyle={COLORS.sl}
                  strokeDasharray={strokeDasharray}
                  strokeWidth={strokeWidth}
                  edge={showSlLine ? edgeSL : {}}
                  textFill={COLORS.sl}
                  fontFamily={this.props.fontFamily}
                  fontSize={this.props.fontSize}
                  onDragStart={(e, moreProps) => this.handleDragStart(e, moreProps, 'SL')}
                  onDrag={this.handleDrag}
                  onDragComplete={(e, moreProps) => this.handleDragComplete(e, moreProps, 'SL')}
                  onHover={this.handleSLTPHover}
                  onUnHover={this.handleSLTPHover}
                />
              );
            })()}
          </>
        )}
        {/* ── 박스(box) 계열: 선보다 나중에 그려서 위에 표시 ── */}
        {renderBoxesOnly && yValue && (
          <>
            <OrderEntryBox
              ref={this.orderEntryBoxRef}
              yValue={yValue}
              positionType={positionType}
              amount={amount}
              isDark={isDark ?? false}
              selected={selected}
              draggable={this.props.draggable && !isEntered}
              rightOffset={textBox.closeIcon.right}
              onDragStart={(e, moreProps) => this.handleDragStart(e, moreProps, 'Entry')}
              onDrag={this.handleDrag}
              onDragComplete={(e, moreProps) => this.handleDragComplete(e, moreProps, 'Entry')}
              onHover={this.handlePositionHover}
              onUnHover={this.handlePositionHover}
              isEntered={isEntered}
              onDelete={(e, moreProps) => this.handleDelete(e, moreProps, 'Entry')}
              onSettingsClick={(e, moreProps) => this.props.onDoubleClick?.(e, this.props.uuid ?? '', moreProps)}
            />

            <EdgeCircleAndVerticalLine
              entryY={yValue}
              tpY={activeTP}
              slY={activeSL}
              tpIsFixed={tpIsFixed}
              slIsFixed={slIsFixed}
              showCircle={showCircle}
              stroke={stroke}
              strokeWidth={strokeWidth}
              fill={stroke}
              entryLeftToYAxisDistance={entryLeftToYAxisDistance}
              ratioRightToYAxisDistance={this.ratioRightToYAxisDistance}
              isHedge={this.props.isHedge}
              hideRatioBox={this.props.hideRatioBox}
              orderEntryBoxRef={this.orderEntryBoxRef}
              tpBoxRef={this.tpBoxRef}
              slBoxRef={this.slBoxRef}
            />

            {/* TP/SL 비율 박스: entry 라인 왼쪽에 ratio 텍스트(예: "2.50") 또는 "TP/SL" 표시 */}
            {!hideRatioBox && (
              <TPSLRatioBox
                ref={this.ratioBoxRef}
                yValue={yValue}
                color={stroke}
                ratioText={selectRatio ?? (tpslRatio > 0 ? tpslRatio.toFixed(2) : 'TP/SL')}
                isDark={isDark ?? false}
                rightOffset={ratioRightOffset}
                onHover={this.onRatioHover}
                onUnHover={this.onRatioHover}
                onClick={this.onRatioClick}
                onDragAreaHover={this.onRatioDragAreaHover}
                onDragStart={
                  this.props.draggable && !isEntered
                    ? (e, moreProps) => this.handleDragStart(e, moreProps, 'Entry')
                    : undefined
                }
                onDrag={this.props.draggable && !isEntered ? this.handleDrag : undefined}
                onDragComplete={
                  this.props.draggable && !isEntered
                    ? (e, moreProps) => this.handleDragComplete(e, moreProps, 'Entry')
                    : undefined
                }
              />
            )}
          </>
        )}

        {/* TP/SL 가격 박스 + 핸들 라벨: TP/SL이 설정된 경우에만 렌더 */}
        {renderBoxesOnly && (
          <>
            {/* TP 박스 → TP 대가리 → SL 박스 → SL 대가리 순서로 배치.
                SL 관련 요소가 항상 TP 관련 요소보다 나중에 그려져 SL 대가리가 위에 표시됨.
                드래그 중 TPAndSLBox가 unmount→remount되면 구독 순서가 뒤로 밀리므로
                tpLine을 TPAndSLBox(TP) 바로 뒤에 붙여 순서를 고정함. */}
            {tpIsFixed && (
              <TPAndSLBox
                ref={this.tpBoxRef}
                yValue={tpIsFixed ? activeTP : yValue}
                type="TP"
                positionType={positionType}
                priceText={
                  tpIsFixed
                    ? `${TP_roe !== undefined ? (TP_roe >= 0 ? '+ ' : '- ') + Math.abs(TP_roe).toFixed(2) + ' %' : ''}`
                    : 'TP/SL'
                }
                rightOffset={entryRightToYAxisDistance}
                draggable={this.props.draggable}
                isDark={isDark}
                onDragStart={(e, moreProps) => this.handleDragStart(e, moreProps, 'TP')}
                onDrag={this.handleDrag}
                onDragComplete={(e, moreProps) => this.handleDragComplete(e, moreProps, 'TP')}
                onHover={this.handleSLTPHover}
                onUnHover={this.handleSLTPHover}
                onDelete={(e, moreProps) => this.handleDelete(e, moreProps, 'TP')}
              />
            )}
            {tpHandle}
            {slIsFixed && (
              <TPAndSLBox
                ref={this.slBoxRef}
                yValue={activeSL}
                type="SL"
                positionType={positionType}
                priceText={
                  slIsFixed
                    ? `${SL_roe !== undefined ? (SL_roe >= 0 ? '+ ' : '- ') + Math.abs(SL_roe).toFixed(2) + ' %' : ''}`
                    : 'TP/SL'
                }
                rightOffset={entryRightToYAxisDistance}
                draggable={this.props.draggable}
                isDark={isDark}
                onDragStart={(e, moreProps) => this.handleDragStart(e, moreProps, 'SL')}
                onDrag={this.handleDrag}
                onDragComplete={(e, moreProps) => this.handleDragComplete(e, moreProps, 'SL')}
                onHover={this.handleSLTPHover}
                onUnHover={this.handleSLTPHover}
                onDelete={(e, moreProps) => this.handleDelete(e, moreProps, 'SL')}
              />
            )}
            {slHandle}
          </>
        )}
      </g>
    );
  }

  // TP 드래그 완료 후 props 업데이트가 비동기로 오는 동안 draggingTPValueRef는 이미 클리어된 상태라
  // 그 사이 TP 라인이 순간적으로 튀거나 깜빡이는 문제가 있었다.
  // updatedTP가 중간 버퍼 역할을 한다:
  //   드래그 중      → draggingTPValueRef로 실시간 렌더
  //   드래그 완료    → draggingTPValueRef 클리어, updatedTP가 props.tp 반영 전까지 유지
  //   props 업데이트 → componentDidUpdate에서 tp 변경 감지 → updatedTP 동기화
  // prevTp === undefined 분기: TP 최초 설정 시 TPdragInProgress=true도 함께 세팅해 박스·핸들 즉시 표시.
  componentDidUpdate(prevProps: EachInteractiveOrderLineProps) {
    const { tpslPrice } = this.props;
    const tp = tpslPrice[0]?.tp || undefined; // 0 = 미설정

    // 외부(onDragComplete/ratio선택 등)에서 tpsl이 업데이트되면 updatedTP 동기화
    const prevTp = prevProps.tpslPrice[0]?.tp || undefined;
    // renderBoxesOnly(boxes 패스)는 드래그를 직접 처리하지 않으므로 isDraggingRef가 부정확함 → 조건 제외
    const skipDraggingCheck = this.props.renderBoxesOnly;
    // BOXES 패스는 드래그 이벤트를 받지 못해 draggingXxxValueRef가 클리어되지 않는다.
    // props가 변경될 때 직접 클리어하여 stale ref 값이 activeSL/activeTP로 사용되는 것을 방지.
    if (skipDraggingCheck) {
      const sl = tpslPrice[0]?.sl || undefined;
      const prevSl = prevProps.tpslPrice[0]?.sl || undefined;
      if (sl !== prevSl) this.draggingSLValueRef = undefined;
      if (tp !== prevTp) this.draggingTPValueRef = undefined;
    }
    if (tp !== undefined && tp !== prevTp && (skipDraggingCheck || !this.isDraggingRef)) {
      if (prevTp === undefined) {
        this.setState({ updatedTP: tp, TPdragInProgress: true });
      } else {
        this.setState({ updatedTP: tp });
      }
    }
  }

  // ─── 렌더 헬퍼: 텍스트 라벨 박스 (가격, 포지션 타입, X 버튼 등) ─
  private readonly renderLabel = ({
    yValue,
    text,
    rightOffset,
    strokeStyle,
    textFill,
    onClick,
    dragType,
    fontStyle,
    onHover,
    onUnHover,
    key,
    bgOverride,
    borderRadius,
    corners,
    fontWeightOverride,
    strokeWidthOverride,
    strokeSides,
  }: {
    yValue: number;
    text: string;
    rightOffset: number;
    strokeStyle: string;
    textFill: string;
    onClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
    dragType: 'SL' | 'TP';
    fontStyle: string;
    onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
    onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
    key?: string;
    bgOverride?: string;
    borderRadius?: number;
    corners?: 'all' | 'left' | 'right';
    fontWeightOverride?: string | number;
    strokeWidthOverride?: number;
    strokeSides?: 'all' | 'no-right';
  }) => {
    const { selected, fontFamily, fontWeight, fontSize, bgFill, uuid, strokeWidth, onDoubleClick, draggable } =
      this.props;
    const dragProps = draggable
      ? {
          onDragStart: (e: React.MouseEvent, moreProps: MoreProps) => this.handleDragStart(e, moreProps, dragType),
          onDrag: this.handleDrag,
          onDragComplete: (e: React.MouseEvent, moreProps: MoreProps) =>
            this.handleDragComplete?.(e, moreProps, dragType),
        }
      : {};

    return (
      <TPAndSLDragHandle
        key={key}
        ref={this.saveNodeType(`label-${key}`)}
        yValue={yValue}
        text={text}
        bgFillStyle={bgOverride ?? bgFill}
        borderRadius={borderRadius}
        corners={corners}
        textFill={textFill}
        fontFamily={fontFamily}
        fontWeight={fontWeightOverride ?? fontWeight}
        fontSize={fontSize}
        fontStyle={fontStyle}
        textBox={{
          height: 24,
          right: rightOffset,
          padding: { left: 6, right: 6 },
        }}
        selected={selected}
        strokeStyle={strokeStyle}
        strokeWidth={strokeWidthOverride ?? strokeWidth}
        strokeSides={strokeSides}
        onHover={onHover}
        onUnHover={isDefined(onUnHover) ? onUnHover : onHover}
        onClick={onClick}
        onDoubleClick={(e: React.MouseEvent, moreProps) => onDoubleClick?.(e, uuid ?? '', moreProps)}
        {...dragProps}
      />
    );
  };

  // ─── 호버 이벤트 핸들러 ─────────────────────────────────────────
  private readonly handleLineHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const hovering = moreProps.hovering ?? false;
    if (this.isDraggingRef) return;
    this.setState({
      lineHover: hovering,
    });
  };

  private readonly handlePositionHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const hovering = moreProps.hovering ?? false;

    if (this.isDraggingRef) return;
    this.setState({
      positionHover: hovering,
    });
  };

  // ─── 삭제: Entry 삭제 시 전체 제거, TP/SL X버튼은 tpslPrice 전체 삭제 ──
  // RFC는 onClick을 모든 GenericChartComponent에 브로드캐스트하므로 Entry/TP/SL 박스가 Y축으로
  // 겹칠 때 하나의 클릭이 여러 handleDelete를 동시에 발화한다.
  // RFC 마운트 순서 때문에 Entry → TP → SL 순으로 호출된다.
  // Entry 삭제를 setTimeout(0)으로 한 틱 미루고, 그 사이 TP/SL이 오면 타이머를 취소한다.
  private readonly handleDelete = (e: React.MouseEvent | null, moreProps: MoreProps, type: 'Entry' | 'TP' | 'SL') => {
    if (type === 'Entry') {
      // isEntered(체결 상태)면 ShapeManager에서 모달을 띄우고 cancel 시 삭제하지 않는다.
      // 지연 실행해도 moreProps 캡처가 필요하므로 콜백으로 감싼다.
      if (this.props.isEntered) {
        clearTimeout(this._entryDeleteTimer);
        this._entryDeleteTimer = setTimeout(() => {
          this._entryDeleteTimer = undefined;
          this.props.onDelete?.(e, this.props.index, moreProps, type);
        }, 0);
        return;
      }
      clearTimeout(this._entryDeleteTimer);
      this._entryDeleteTimer = setTimeout(() => {
        this._entryDeleteTimer = undefined;
        this.setState({ TPdragInProgress: false, SLdragInProgress: false });
        this.props.onDelete?.(e, this.props.index, moreProps, type);
      }, 0);
    } else {
      // TP든 SL이든 X버튼 → Entry 타이머를 취소하고 tpsl만 삭제
      clearTimeout(this._entryDeleteTimer);
      this._entryDeleteTimer = undefined;
      this.setState({ TPdragInProgress: false, SLdragInProgress: false, updatedTP: undefined, tpslRatio: 0 });
      this.props.onDelete?.(e, this.props.index, moreProps, type);
    }
  };

  // ─── 드래그: 라인별 유효성 검증 + TP:SL 비율 실시간 계산 ────────
  private readonly handleDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { index, onDrag, positionType, yValue, amount, tpslPrice } = this.props;
    const tp = tpslPrice[0]?.tp || undefined; // 0 = 미설정
    const sl = tpslPrice[0]?.sl || undefined; // 0 = 미설정
    const { updatedTP } = this.state;
    if (!onDrag) return;

    const {
      mouseXY: [, mouseY],
      chartConfig: { yScale },
    } = moreProps;

    if (!this.dragStartPosition) return;
    const { dy, type } = this.dragStartPosition;

    let newYValue = yScale.invert(mouseY - dy);
    if (type === 'TP') {
      const dirValid =
        (positionType === 'long' && newYValue > yValue) || (positionType === 'short' && newYValue < yValue);
      if (!dirValid) return;
      // TP_roe < 0.1%이면 fee를 못 커버 → 0.1%에 해당하는 최소 가격으로 clamp
      // 드래그는 계속 허용하되 라인은 최소값에 고정, 마우스가 다시 유효 범위로 오면 따라옴
      const minTPPrice = netRoeToPrice({ netRoe: 0.1, entry: yValue, amount, positionType });
      newYValue = positionType === 'long' ? Math.max(newYValue, minTPPrice) : Math.min(newYValue, minTPPrice);
      this.draggingTPValueRef = newYValue;
      if (!this.isDraggingRef) {
        this.isDraggingRef = true;
        this.setState({ updatedTP: undefined, isSLTPHover: false, positionHover: false });
      }
    }

    if (type === 'SL') {
      const isValid =
        (positionType === 'long' && newYValue < yValue) || (positionType === 'short' && newYValue > yValue);

      if (!isValid) return; // Prevent drag if invalid
      this.draggingSLValueRef = newYValue;
      if (!this.isDraggingRef) {
        this.isDraggingRef = true;
        this.setState({ isSLTPHover: false, positionHover: false });
      }
    }

    // Entry 드래그 시 TP/SL을 netROE 기반으로 재계산하여 ratio를 정확히 유지한다.
    // 가격 delta 방식은 entry 변경 시 positionSize가 달라져 ROE%가 미세하게 흔들림.
    if (type === 'Entry') {
      const currentSL = sl;
      const currentTP = updatedTP ?? tp;
      if (currentSL !== undefined && currentSL !== 0 && amount > 0) {
        const slRoe = priceToNetRoe({ price: currentSL, entry: yValue, amount, positionType });
        this.draggingSLValueRef = netRoeToPrice({ netRoe: slRoe, entry: newYValue, amount, positionType });
        if (currentTP !== undefined && currentTP !== 0) {
          const tpRoe = priceToNetRoe({ price: currentTP, entry: yValue, amount, positionType });
          this.draggingTPValueRef = netRoeToPrice({ netRoe: tpRoe, entry: newYValue, amount, positionType });
        }
      } else if (currentTP !== undefined && currentTP !== 0 && amount > 0) {
        const tpRoe = priceToNetRoe({ price: currentTP, entry: yValue, amount, positionType });
        this.draggingTPValueRef = netRoeToPrice({ netRoe: tpRoe, entry: newYValue, amount, positionType });
      }
    }

    if (type === 'Entry' && (this.state.SLdragInProgress || this.state.TPdragInProgress)) {
      let isValid = true;
      const slVal = sl ?? yValue;
      const tpVal = updatedTP ?? tp ?? yValue;
      if (positionType === 'long') {
        if (newYValue <= slVal) isValid = false;
        if (newYValue >= tpVal && this.state.TPdragInProgress) isValid = false;
      } else if (positionType === 'short') {
        if (newYValue >= slVal) isValid = false;
        if (newYValue <= tpVal && this.state.TPdragInProgress) isValid = false;
      }

      if (!isValid) return; // Prevent drag if going outside SL/TP range
    }

    // 드래그 중인 값을 즉시 반영하여 ratio를 계산한다.
    // this.state.TP_roe / SL_roe는 이전 렌더 사이클의 값이므로 사용하면 1프레임 지연됨.
    // TP가 명시적으로 삭제된 상태에서는 ratio 계산을 건너뜀 (삭제된 TP 위치로 비율이 표시되는 문제 방지)
    const currentTP = type === 'TP' ? newYValue : (updatedTP ?? tp);
    const currentSL = type === 'SL' ? newYValue : sl;
    const currentEntry = type === 'Entry' ? newYValue : yValue;
    if (currentTP != null && currentSL != null && amount > 0) {
      try {
        const { TP_roe: freshTP, SL_roe: freshSL } = calculateTPAndSL({
          price: currentEntry,
          amount,
          tp: currentTP,
          sl: currentSL,
          positionType,
          type: ShapeType.ORDER_LINE,
        });
        if (freshSL !== 0) {
          const tpslRatio = TPSLRatio(freshSL, freshTP);
          this.setState({ tpslRatio });
        }
      } catch {
        // amount 또는 가격이 유효하지 않은 경우 무시
      }
    }
    onDrag(e, index, newYValue, type);
  };

  private readonly handleDragStart = (_: React.MouseEvent, moreProps: MoreProps, type: 'Entry' | 'TP' | 'SL') => {
    const { yValue, tpslPrice } = this.props;
    const tp = tpslPrice[0]?.tp;
    const sl = tpslPrice[0]?.sl;
    const {
      mouseXY,
      chartConfig: { yScale },
    } = moreProps;
    const [, mouseY] = mouseXY;
    let value = yValue;
    if (type === 'SL') {
      // Entry 박스와 SL 핸들이 같은 y 좌표에 있을 때 RFC가 둘 다 onDragStart를 전파한다.
      // Entry 박스가 hover 상태라면 이 SL dragStart는 Entry 클릭의 부산물이므로 무시한다.
      if (this.state.positionHover || this.state.lineHover) return;
      const wasAlreadyInProgress = this.state.SLdragInProgress;
      value = wasAlreadyInProgress ? sl : yValue;
      this.draggingSLValueRef = value;
      this.setState({ SLdragInProgress: true });

      // react-financial-charts는 마우스가 움직이지 않으면 onDragComplete를 호출하지 않음.
      // pointerup에서 실제 이동 여부를 확인해 제자리 드롭 시 SLdragInProgress를 복원.
      const startValue = value;
      this.slPointerUpCleanup?.();
      const onPointerUp = () => {
        this.slPointerUpCleanup = undefined;
        document.removeEventListener('pointerup', onPointerUp);
        // activeDragType이 SL이 아니면 다른 컴포넌트(Entry 등)의 mouseup이 트리거한 것 — 무시
        if (this.activeDragType !== 'SL') return;
        const moved = this.draggingSLValueRef !== undefined && this.draggingSLValueRef !== startValue;
        if (!moved) {
          // 제자리 드롭: SLdragInProgress를 원래 상태로 복원
          this.draggingSLValueRef = undefined;
          this.isDraggingRef = false;
          this.setState({ SLdragInProgress: wasAlreadyInProgress });
        }
      };
      document.addEventListener('pointerup', onPointerUp);
      this.slPointerUpCleanup = () => document.removeEventListener('pointerup', onPointerUp);
    }
    if (type === 'TP') {
      // Entry 박스와 TP 핸들이 같은 y 좌표에 있을 때 RFC가 둘 다 onDragStart를 전파한다.
      // Entry 박스가 hover 상태라면 이 TP dragStart는 Entry 클릭의 부산물이므로 무시한다.
      if (this.state.positionHover || this.state.lineHover) return;
      const wasAlreadyInProgress = this.state.TPdragInProgress;
      // tp가 0이거나 없으면(미설정/삭제) entry 위치에서 드래그 시작
      const existingTP = this.state.updatedTP ?? (tp !== undefined && tp !== 0 ? tp : undefined);
      value = existingTP ?? yValue;
      this.setState({ TPdragInProgress: true });
      this.draggingTPValueRef = value;

      // react-financial-charts는 마우스가 움직이지 않으면 onDragComplete를 호출하지 않음.
      // pointerup에서 실제 이동 여부를 확인해 제자리 드롭 시 TPdragInProgress를 복원.
      const startValue = value;
      this.tpPointerUpCleanup?.();
      const onPointerUp = () => {
        this.tpPointerUpCleanup = undefined;
        document.removeEventListener('pointerup', onPointerUp);
        // activeDragType이 TP가 아니면 다른 컴포넌트(Entry 등)의 mouseup이 트리거한 것 — 무시
        if (this.activeDragType !== 'TP') return;
        const moved = this.draggingTPValueRef !== undefined && this.draggingTPValueRef !== startValue;
        if (!moved) {
          // 제자리 드롭: TPdragInProgress를 원래 상태로 복원
          this.draggingTPValueRef = undefined;
          this.isDraggingRef = false;
          this.setState({ TPdragInProgress: wasAlreadyInProgress });
        }
      };
      document.addEventListener('pointerup', onPointerUp);
      this.tpPointerUpCleanup = () => document.removeEventListener('pointerup', onPointerUp);
    }
    if (type === 'Entry') {
      // isDraggingRef는 실제 마우스 이동 시(handleDrag)에서만 true로 설정한다.
      // 여기서 설정하면 클릭만 해도 isDraggingRef=true가 고착되어 이후 hover 핸들러가 모두 막힌다.
    }
    if (type === 'Entry' && !this.state.SLdragInProgress && !this.state.TPdragInProgress) {
      this.setState({
        lineHover: false,
        positionHover: false,
      });
    }

    this.activeDragType = type;
    this.dragStartPosition = { yValue: value, dy: mouseY - yScale(value), type };
  };

  // ─── 드래그 완료: SL 최초 드래그 시 기본 비율(2.0)로 TP 자동 계산 ─
  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps, type: 'Entry' | 'TP' | 'SL') => {
    const { tpslRatio } = this.state;
    if (type === 'SL' && !this.state.TPdragInProgress && this.isDraggingRef) {
      const { yValue, amount, tpslPrice: propsTpsl, positionType } = this.props;
      // draggingSLValueRef: 드래그 완료 시점의 실시간 값 (props.sl은 아직 이전 값일 수 있음)
      const sl = this.draggingSLValueRef ?? propsTpsl[0]?.sl;

      // SL 최초 드래그 완료 시 로컬스토리지의 방향별 마지막 비율로 TP를 계산한다.
      // 헷지 상태이면 hedge 비율을 우선 사용, 아니면 방향별 비율 사용.
      const dir = positionType === 'short' ? 'short' : 'long';
      const { lastTPSLByDirection } = globalShapeStore.getState();
      const isHedgeRatioActive = this.props.isHedge && lastTPSLByDirection.long === lastTPSLByDirection.short;
      const defaultRatio = isHedgeRatioActive ? lastTPSLByDirection.hedge || 2.0 : lastTPSLByDirection[dir] || 2.0;
      const newTP = calculateTPFromRatio({
        TP_SL_ratio: defaultRatio,
        price: yValue,
        amount,
        sl: sl,
        positionType,
      });

      // SL 최초 드래그 완료 시 결정된 비율을 저장 (경로 1)
      // 헷지 상태에서 SL 최초 배치는 공통 ratio 박스를 유지해야 하므로 dir만 갱신.
      // (defaultRatio가 hedge 비율에서 왔으므로 long === short 조건이 깨지지 않음)
      globalShapeStore.getState().updateLastTPSLByDirection(dir, defaultRatio);

      this.setState(
        {
          SLDragCompleted: true,
          updatedTP: newTP,
          tpslRatio: defaultRatio,
        },
        () => {
          this.setState({ TPdragInProgress: true }, () => {
            this.props.onDragComplete?.(e, moreProps, type);
          });
        },
      );

      return;
    }

    if (type === 'SL' || type === 'TP' || type === 'Entry') {
      // activeDragType이 현재 type과 다르면 다른 드래그가 진행 중인 상태에서 끼어든 완료 이벤트 — 무시
      if (this.activeDragType !== type) return;

      // TP/SL/Entry 드래그 완료 시 실시간으로 역산된 비율을 로컬스토리지에 저장 (경로 3)
      // tpslRatio는 handleDrag에서 매 프레임 역산되므로 드롭 순간의 값이 여기 담겨 있다.
      if (tpslRatio > 0) {
        const { positionType } = this.props;
        const dir = positionType === 'short' ? 'short' : 'long';
        const { lastTPSLByDirection } = globalShapeStore.getState();
        const isHedgeRatioActive = this.props.isHedge && lastTPSLByDirection.long === lastTPSLByDirection.short;
        if (isHedgeRatioActive) {
          // 헷지 상태에서 조작: 조작한 쪽 개별 비율 갱신 → long !== short가 되어 개별 쪼개짐
          // 반대쪽은 hedge 비율로 개별 비율 갱신 (가만히 있던 쪽은 hedge 비율 그대로)
          const oppositeDir = dir === 'short' ? 'long' : 'short';
          globalShapeStore.getState().updateLastTPSLByDirection(oppositeDir, lastTPSLByDirection.hedge);
          globalShapeStore.getState().updateLastTPSLByDirection(dir, tpslRatio);
        } else {
          globalShapeStore.getState().updateLastTPSLByDirection(dir, tpslRatio);
        }
      }
      this.setState({ SLDragCompleted: true }, () => {
        this.props.onDragComplete?.(e, moreProps, type);
      });
    }

    // activeDragType이 현재 완료된 드래그 타입과 일치할 때만 ref를 초기화한다.
    // Entry 라인 클릭 → SL 드래그로 이어질 때 RFC가 Entry의 onDragComplete를 SL 드래그 도중에
    // 끼워 넣을 수 있는데, 이 경우 draggingSLValueRef를 지우면 SL 드래그 중 렌더가 깨진다.
    if (this.activeDragType === type) {
      this.draggingSLValueRef = undefined;
      this.draggingTPValueRef = undefined;
      this.isDraggingRef = false;
      this.activeDragType = undefined;
      // 드래그 완료 후 호버 상태 리셋 — 드래그 중 isDraggingRef 가드로 onUnHover가 막혀
      // isSLTPHover/positionHover가 true로 고착되어 서클이 계속 표시되는 문제 방지.
      this.setState({ isSLTPHover: false, positionHover: false });
    }
  };

  // ─── TP/SL 영역 호버 ────────────────────────────────────────────
  private readonly handleSLTPHover = (_e: React.MouseEvent, moreProps: MoreProps) => {
    if (this.isDraggingRef) return;
    if (this.state.isSLTPHover !== moreProps.hovering) {
      this.setState({
        lineHover: false,
        positionHover: false,
        isSLTPHover: moreProps.hovering ?? false,
      });
    }
  };

  // ─── ratio 박스 클릭: 드롭다운 열기 ──────────────────────────────
  private readonly onRatioClick = (_e: React.MouseEvent, moreProps: MoreProps) => {
    if (!moreProps.hovering) return;
    const { setRatioDropdown, uuid, positionType, stroke, yValue } = this.props;
    const {
      chartConfig: { yScale },
    } = moreProps;
    const boxX = this.ratioBoxRef.current?.boxX ?? 0;
    const boxY = Math.round(yScale(yValue)) - 12; // BOX_HEIGHT/2 = 12
    const position = { x: boxX, y: boxY };
    this.setState({ showRatioDropdown: true });
    setRatioDropdown({ uuid: uuid ?? '', hovering: true, position, positionType, color: stroke });
  };

  // ─── ratio 박스 전체 영역 호버: 서클 표시만 (드롭다운 없음) ────────
  // 드래그 전용 GenericChartComponent(전체 박스 히트)에 연결 — setRatioDropdown 호출 금지
  private readonly onRatioDragAreaHover = (_e: React.MouseEvent, moreProps: MoreProps) => {
    const hovering = moreProps.hovering ?? false;
    if (this.state.showRatioDragArea !== hovering) {
      this.setState({ showRatioDragArea: hovering });
    }
  };

  // ─── ratio 박스 호버: 드롭다운 표시/숨김 ─────────────────────────
  private readonly onRatioHover = (_e: React.MouseEvent, moreProps: MoreProps) => {
    const { setRatioDropdown, uuid, positionType, stroke, yValue } = this.props;
    const hovering = moreProps.hovering ?? false;
    if (this.state.showRatioDropdown !== hovering) {
      this.setState({ showRatioDropdown: hovering });
      const {
        chartConfig: { yScale },
      } = moreProps;
      const boxX = this.ratioBoxRef.current?.boxX ?? 0;
      const boxY = Math.round(yScale(yValue)) - 12;
      const position = { x: boxX, y: boxY };
      setRatioDropdown({ uuid: uuid ?? '', hovering, position, positionType, color: stroke });
    }
  };

  static getDerivedStateFromProps(nextProps: EachInteractiveOrderLineProps, prevState: EachInteractiveOrderLineState) {
    const { yValue, amount, tpslPrice, positionType, isHedge } = nextProps;
    const tp = tpslPrice[0]?.tp || undefined; // 0 = 미설정
    const sl = tpslPrice[0]?.sl || undefined; // 0 = 미설정
    const updatedTP = prevState.updatedTP ?? tp;

    const hasChanged =
      yValue !== prevState.prevYValue ||
      sl !== prevState.prevSL ||
      updatedTP !== prevState.prevTP ||
      amount !== prevState.prevAmount ||
      positionType !== prevState.prevPositionType;

    // isHedge 변경 시 (롱만 있다가 숏 추가로 hedge가 되거나 반대) → 비율을 방향에 맞게 갱신
    const hedgeChanged = isHedge !== prevState.prevIsHedge;
    let tpslRatioFromHedgeChange: number | undefined;
    if (hedgeChanged && prevState.tpslRatio === 0) {
      // tpslRatio가 아직 0인 경우에만 갱신 (사용자가 직접 조정한 비율은 건드리지 않음)
      const { lastTPSLByDirection } = globalShapeStore.getState();
      const dir = positionType === 'short' ? 'short' : 'long';
      const isHedgeRatioActive = isHedge && lastTPSLByDirection.long === lastTPSLByDirection.short;
      tpslRatioFromHedgeChange = isHedgeRatioActive
        ? (lastTPSLByDirection.hedge ?? 2)
        : (lastTPSLByDirection[dir] ?? 2);
    }

    if (hasChanged || hedgeChanged) {
      const { TP_roe, SL_roe } = calculateTPAndSL({
        price: yValue,
        amount,
        tp: updatedTP,
        sl,
        positionType,
        type: ShapeType.ORDER_LINE,
      });

      // TP_roe, SL_roe가 유효하면 tpslRatio도 함께 갱신한다.
      // 초기 로딩 시 tpslRatio가 0으로 남아 빈 박스가 표시되는 문제를 방지.
      let tpslRatio = tpslRatioFromHedgeChange ?? prevState.tpslRatio;
      if (SL_roe !== 0 && TP_roe !== 0) {
        try {
          tpslRatio = TPSLRatio(SL_roe, TP_roe);
        } catch {
          // ignore
        }
      } else if (tpslRatio === 0) {
        // tpslPrice 미설정(ENTER 직후 등) 상태에서 tpslRatio가 0이면 lastTPSLByDirection에서 fallback
        const { lastTPSLByDirection } = globalShapeStore.getState();
        const dir = positionType === 'short' ? 'short' : 'long';
        const isHedgeRatioActive = isHedge && lastTPSLByDirection.long === lastTPSLByDirection.short;
        tpslRatio = isHedgeRatioActive ? (lastTPSLByDirection.hedge ?? 2) : (lastTPSLByDirection[dir] ?? 2);
      }

      return {
        TP_roe,
        SL_roe,
        tpslRatio,
        prevYValue: yValue,
        prevSL: sl,
        prevTP: updatedTP,
        prevAmount: amount,
        prevPositionType: positionType,
        prevIsHedge: isHedge,
      };
    }

    return null;
  }
}
